/**
 * Centralized RBAC hooks for UI permission gating.
 *
 * These hooks wrap the PermissionContext with scope-aware helpers
 * so components don't need to import multiple modules.
 */

import { useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import type { UserPermissions } from '../pages/users/types/permissions';
import type { Role } from '../pages/users/types/rbac';
import type { ResourceAction, ResourceScope, ResourceType } from '../lib/rbac/scopeAccess';
import {
  canAccess,
  canPerformWorkflow,
  getDefaultScope,
  getViewScope,
  getEditScope,
} from '../lib/rbac/scopeAccess';

// ---------------------------------------------------------------------------
// useHasPermission — boolean permission key check
// ---------------------------------------------------------------------------

export function useHasPermission(key: keyof UserPermissions): boolean {
  const rbac = usePermissions();
  return rbac.permissions[key] === true;
}

export function useHasAnyPermission(...keys: (keyof UserPermissions)[]): boolean {
  const rbac = usePermissions();
  return keys.some((k) => rbac.permissions[k] === true);
}

export function useHasAllPermissions(...keys: (keyof UserPermissions)[]): boolean {
  const rbac = usePermissions();
  return keys.every((k) => rbac.permissions[k] === true);
}

// ---------------------------------------------------------------------------
// useCanAccess — scope-aware resource check
// ---------------------------------------------------------------------------

export function useCanAccess(
  resource: ResourceType,
  action: ResourceAction,
  requiredScope: ResourceScope = 'OWN',
): boolean {
  const rbac = usePermissions();
  return useMemo(
    () => canAccess(rbac.role, resource, action, requiredScope),
    [rbac.role, resource, action, requiredScope],
  );
}

// ---------------------------------------------------------------------------
// useCanPerformWorkflow — named workflow check
// ---------------------------------------------------------------------------

export function useCanPerformWorkflow(workflowKey: string): boolean {
  const rbac = usePermissions();
  return useMemo(
    () => canPerformWorkflow(rbac.role, workflowKey),
    [rbac.role, workflowKey],
  );
}

// ---------------------------------------------------------------------------
// useCurrentScope — what scope does the user operate at?
// ---------------------------------------------------------------------------

export function useCurrentScope(): {
  role: Role;
  defaultScope: ResourceScope;
  viewScope: ResourceScope;
  editScope: ResourceScope;
} {
  const rbac = usePermissions();
  return useMemo(() => ({
    role: rbac.role,
    defaultScope: getDefaultScope(rbac.role),
    viewScope: getViewScope(rbac.role),
    editScope: getEditScope(rbac.role),
  }), [rbac.role]);
}

// ---------------------------------------------------------------------------
// useRoleFlags — convenience booleans for common role checks
// ---------------------------------------------------------------------------

export function useRoleFlags(): {
  isAdmin: boolean;
  isSupervisor: boolean;
  isProvider: boolean;
  isClient: boolean;
  isPracticeOwner: boolean;
  isStaff: boolean;
  role: Role;
} {
  const rbac = usePermissions();
  return useMemo(() => ({
    isAdmin: rbac.role === 'unify-admin',
    isSupervisor: rbac.role === 'supervisor',
    isProvider: rbac.role === 'provider' || rbac.role === 'supervisor',
    isClient: rbac.isClient,
    isPracticeOwner: rbac.role === 'practice-owner',
    isStaff: rbac.isStaff,
    role: rbac.role,
  }), [rbac.role, rbac.isClient, rbac.isStaff]);
}
